import React, { useEffect, useState } from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import { SelectedItemDetails } from '@/components/modules/trip-route-details/trip-route-details';
import CtaLinkList from '@/components/organisms/cta-link-list/cta-link-list';
import ImageSlider from '@/components/organisms/image-slider/image-slider';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import styles from './trip-route-details-modal.module.scss';

export interface TripRouteDetailsModalProps {
  /** The details to show in the modal */
  details: SelectedItemDetails;
  /** Whether the modal is open or not */
  isOpen?: boolean;
  /** The callback to change the state of the modal */
  setIsOpen?: (value: boolean) => void;
}

const TripRouteDetailsModal = ({
  details,
  isOpen = false,
  setIsOpen,
}: TripRouteDetailsModalProps) => {
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [resetPosition, setResetPosition] = useState(false);

  const handleCloseModal = () => {
    setIsOpen?.(false);
    setResetPosition(true);
    setActiveItemIndex(0);
  };

  useEffect(() => {
    if (!isOpen) return;

    setResetPosition(false);
  }, [isOpen]);

  return (
    <Lightbox
      className={styles.component}
      isCentered
      isFullWidthOnMobile
      isOpen={isOpen}
      onClose={handleCloseModal}
      wrapperClassName={styles.lightboxWrapper}
    >
      <div className={styles.lightboxInnerWrapper}>
        <div className={styles.contentWrapper}>
          <div className={styles.details}>
            <div className={styles.headlineWrapper}>
              {details.preHeadline && (
                <Typography
                  className={styles.preHeadline}
                  tagStyle="labelMedium"
                >
                  {details.preHeadline}
                </Typography>
              )}

              {details.headline && (
                <Typography
                  className={styles.headline}
                  tagStyle="displayMedium"
                >
                  {details.headline}
                </Typography>
              )}
            </div>

            {details.subHeadline && (
              <Typography
                className={styles.subHeadline}
                tagStyle="bodyLargeBold"
              >
                {details.subHeadline}
              </Typography>
            )}

            {details.text && (
              <RichText className={styles.text} text={details.text} />
            )}

            {details.ctaLinks && details.ctaLinks.length > 0 && (
              <CtaLinkList items={details.ctaLinks} />
            )}
          </div>

          {details.images && details.images.length > 0 && (
            <div className={styles.imageWrapper}>
              <ImageSlider
                key={details.id}
                className={styles.imageSlider}
                compact
                images={details.images}
                onActiveIndexChange={index => setActiveItemIndex(index)}
                resetPosition={resetPosition}
              />

              <div className={styles.imageInfo}>
                {details.imageCaption && (
                  <Typography className={styles.caption} tagStyle="labelMedium">
                    {details.imageCaption}
                  </Typography>
                )}

                <Typography className={styles.navigation} tagStyle="labelSmall">
                  {`${activeItemIndex + 1} / ${details.images.length}`}
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>
    </Lightbox>
  );
};

TripRouteDetailsModal.displayName = 'TripRouteDetailsModal';

export default TripRouteDetailsModal;
